/**
 * Keeps the "Programmed & Ready" register lines and their PROGRAM-60 service line honest.
 *
 * WHY THIS EXISTS
 *
 * "Programmed & Ready" (snippets/sam4s-register-buy-choice.liquid) adds two independent cart
 * lines: the register, tagged `Register Setup: Programmed & Ready`, and the existing PROGRAM-60
 * service, tagged `Programming for: <model>`. Shopify carts have no concept of a bundle, so the
 * customer can then change either line on its own, which produced two wrong orders (reproduced
 * live 2026-10-23 on the ER-180U):
 *
 *   - remove the register  -> a lone $100 programming charge stays in the cart and can be paid
 *   - remove programming   -> the register still says "Programmed & Ready" at the register price,
 *                             and the CRS / M-S auto-PO lane reads that label
 *   - raise the register quantity -> N registers labelled programmed, fewer programming hours paid
 *
 * WHAT IT DOES (and does not)
 *
 *   - Never adds programming or increases the requested hardware count. Every correction either removes a service line that no register
 *     justifies, or relabels register units that no programming line pays for as `Register Only`.
 *   - Never touches a PROGRAM-60 line without the `Programming for` tag, so a customer who buys
 *     programming on its own, or buys extra hours on top of a bundled register, is left alone
 *     (more programming than registers is deliberate, never reduced).
 *   - Pairs only through the approved exact SKU-to-model map; titles and totals never establish identity.
 *
 * This is a storefront correction, not a server-side guarantee: Shopify Functions cart
 * validation is the durable fix and needs an app. It runs on every cart page load and after any
 * cart change request the theme makes.
 */

export const PROGRAM_SKU = 'PROGRAM-60';
export const PROGRAMMED = 'Programmed & Ready';
export const REGISTER_ONLY = 'Register Only';

const norm = (s) => String(s || '').trim().toUpperCase();

export const REGISTER_MODELS = {'950090':'ER-920E','950091':'ER-925E','950092':'ER-915E','950093':'ER-940E','950094':'ER-945E','950085':'ER-265EJ','950084-S4D':'ER-260EJ','950095':'NR-510BE','950096':'NR-510RBE','950080':'ER-180U','950027':'SPS-320','950037':'SPS-340','950060':'SPS-345'};
const knownModels = new Set(Object.values(REGISTER_MODELS));
const isBundledProgram = (i) => i && i.sku === PROGRAM_SKU && i.properties && knownModels.has(norm(i.properties['Programming for']));
const isProgrammedRegister = (i) => i && REGISTER_MODELS[i.sku] && i.properties && i.properties['Register Setup'] === PROGRAMMED;
const identity = (properties) => JSON.stringify(Object.entries(properties || {}).filter(([key]) => key !== 'Register Setup').sort(([a],[b]) => a.localeCompare(b)));
const units = (items, id, properties) => items.filter(i => (i.variant_id || i.id) === id && identity(i.properties) === identity(properties)).reduce((n,i) => n+i.quantity,0);

/**
 * @param {{items: object[]}} cart  the /cart.js payload
 * @returns {{actions: {op: 'change'|'add', key?: string, id?: number, quantity: number, properties?: object}[], notes: string[]}}
 */
export function plan(cart) {
  const items = (cart && cart.items) || [];
  const programs = items.filter(isBundledProgram);
  const registers = items.filter(isProgrammedRegister);
  const actions = [];
  const notes = [];
  if (!programs.length && !registers.length) return { actions, notes };

  const models = new Set([...programs.map(p => norm(p.properties['Programming for'])), ...registers.map(r => REGISTER_MODELS[r.sku])]);
  const groups = [...models].map(model => ({
    programs: programs.filter(p => norm(p.properties['Programming for']) === model),
    registers: registers.filter(r => REGISTER_MODELS[r.sku] === model),
  }));

  for (const g of groups) {
    const regQty = g.registers.reduce((n, r) => n + r.quantity, 0);
    const progQty = g.programs.reduce((n, p) => n + p.quantity, 0);

    if (regQty === 0 && progQty > 0) {
      for (const p of g.programs) actions.push({ op: 'change', key: p.key, quantity: 0 });
      notes.push('A programming service was removed because the register it belonged to is no longer in your cart.');
      continue;
    }

    if (regQty > progQty) {
      let surplus = regQty - progQty;
      for (const r of [...g.registers].reverse()) {
        if (surplus <= 0) break;
        const take = Math.min(r.quantity, surplus);
        const properties = { ...r.properties, 'Register Setup': REGISTER_ONLY };
        if (take === r.quantity) {
          actions.push({ op: 'change', key: r.key, quantity: r.quantity, properties });
        } else {
          actions.push({ op: 'change', key: r.key, quantity: r.quantity - take, restore: { id:r.variant_id || r.id, quantity:take, properties:r.properties, beforeUnits:units(items,r.variant_id || r.id,r.properties) } });
          actions.push({ op: 'add', id: r.variant_id || r.id, quantity: take, properties });
        }
        surplus -= take;
      }
      notes.push('Some registers no longer had a programming service with them, so they are now listed as Register Only. Nothing was added to your total.');
    }
  }
  return { actions, notes };
}

const post = (fetchImpl, url, body) => fetchImpl(url, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  body: JSON.stringify(body),
});

/** Runs a plan with the given fetch. Returns true when every action succeeded. */
export async function apply(actions, fetchImpl) {
  let pending = null;
  try {
    for (const a of actions) {
      if (a.restore) pending = a.restore;
      const res = a.op === 'add'
        ? await post(fetchImpl, '/cart/add.js', { items: [{ id: a.id, quantity: a.quantity, properties: a.properties }] })
        : await post(fetchImpl, '/cart/change.js', { id: a.key, quantity: a.quantity, ...(a.properties ? { properties: a.properties } : {}) });
      if (!res.ok) throw new Error('cart update refused');
      if (a.op === 'add') pending = null;
    }
    return true;
  } catch (error) {
    if (pending) {
      // An add can commit even when its response is lost. Read back first; only
      // restore the exact known deficit. No blind retry of a cumulative add.
      try {
        const res = await fetchImpl('/cart.js', {headers:{Accept:'application/json'}});
        if (res.ok) {
          const current = await res.json();
          const deficit = pending.beforeUnits - units(current.items || [],pending.id,pending.properties);
          if (deficit === pending.quantity) {
            await post(fetchImpl, '/cart/add.js', {items:[{id:pending.id,quantity:deficit,properties:pending.properties}]});
          }
        }
      } catch (restoreError) { /* uncertain state is surfaced and checkout paused */ }
    }
    return false;
  }
}

/** Serialize native writes and keep writes started during a repair queued. */
export function createCartWriteQueue(rawFetch, onWrite) {
  let tail=Promise.resolve(), active=null, locked=false, release=null, generation=0;
  let ready=Promise.resolve();
  return {
    fetch(input,init) {
      const url=typeof input==='string'?input:(input && input.url)||'';
      if (!/\/cart\/(change|update|add)(\.js)?(\?|$)/.test(url)) return rawFetch(input,init);
      const version=generation;
      const result=tail.catch(()=>{}).then(async()=>{
        if (locked) await ready;
        if(version!==generation)throw new Error('Your cart changed while checking setup. Reload and try again.');
        active=rawFetch(input,init);
        try {return await active;} finally {active=null;onWrite();}
      });
      tail=result;return result;
    },
    async lock() {locked=true;ready=new Promise(resolve=>{release=resolve;});if(active)await active.catch(()=>{});},
    unlock(changed=false) {if(changed)generation+=1;locked=false;if(release)release();release=null;},
  };
}

const NOTE_KEY = 's4s_prog_sync_note';

function boot() {
  const rawFetch=window.fetch.bind(window);
  let running=false, blocked=false, dirty=false, timer=0;
  const schedule=()=>{dirty=true;clearTimeout(timer);timer=setTimeout(reconcile,400);};
  const queue=createCartWriteQueue(rawFetch,schedule);
  window.fetch=queue.fetch;
  const root=document.documentElement;
  const style=document.createElement('style');
  style.textContent='.s4s-prog-sync-busy [data-shopify="dynamic-checkout-cart"],.s4s-prog-sync-busy shopify-accelerated-checkout-cart{visibility:hidden;pointer-events:none}';
  document.head.appendChild(style);
  function guard(event) {
    if ((running||blocked) && event.target.closest('[name="checkout"],a[href*="/checkout"],ecx-cart-frm,ecx-cart-drwr-frm')) {event.preventDefault();event.stopImmediatePropagation();}
  }
  document.addEventListener('click',guard,true);
  document.addEventListener('change',guard,true);
  document.addEventListener('submit',guard,true);
  function notice(text,error=false) {
    const host=document.querySelector('#mainCartContainer')||document.querySelector('main');
    if(!host)return;
    const el=document.createElement('p');el.setAttribute('role',error?'alert':'status');
    el.className='s4s-prog-sync-note';
    el.style.cssText='margin:0 0 16px;padding:12px 14px;border:1px solid #9fb3c8;border-radius:8px;background:#eef6fc;color:#102a43;font-size:14px;line-height:1.5';
    el.textContent=text;
    if(error){const link=document.createElement('a');link.href='/cart';link.textContent=' Reload your cart';el.appendChild(link);}
    host.insertBefore(el,host.firstChild);return el;
  }
  function freeze(value) {
    if(value)document.querySelectorAll('ecx-cart-frm,ecx-cart-drwr-frm,[name="checkout"],a[href*="/checkout"]').forEach(node=>{
      if(!node.hasAttribute('inert')){node.setAttribute('data-s4s-sync-frozen','1');node.setAttribute('inert','');}
    });
    else document.querySelectorAll('[data-s4s-sync-frozen="1"]').forEach(node=>{node.removeAttribute('inert');node.removeAttribute('data-s4s-sync-frozen');});
  }
  async function reconcile() {
    if(running){dirty=true;return;}
    running=true;dirty=false;root.classList.add('s4s-prog-sync-busy');freeze(true);
    const busyNotice=notice('Checking your register setup...');let changed=false;
    await queue.lock();
    try {
      const res=await rawFetch('/cart.js',{headers:{Accept:'application/json'}});
      if(!res.ok)throw new Error('cart unavailable');
      const {actions,notes}=plan(await res.json());
      if(actions.length){
        changed=true;
        if(!await apply(actions,rawFetch))throw new Error('cart correction incomplete');
        try{sessionStorage.setItem(NOTE_KEY,notes.join(' '));}catch(error){}
        window.location.reload();
      }
    } catch(error) {
      blocked=true;
      notice("We couldn't finish updating your setup. Checkout is paused. Reload your cart and check the register quantities before continuing.",true);
    } finally {
      running=false;queue.unlock(changed||blocked);if(busyNotice)busyNotice.remove();if(!blocked){root.classList.remove('s4s-prog-sync-busy');freeze(false);}
      if(dirty&&!blocked)schedule();
    }
  }
  if(/^\/cart(\/|$)/.test(window.location.pathname))schedule();
  let note='';try{note=sessionStorage.getItem(NOTE_KEY)||'';sessionStorage.removeItem(NOTE_KEY);}catch(error){}
  if(note)notice(note);
}
if (typeof window !== 'undefined' && typeof document !== 'undefined') boot();
